import { useQueryClient } from '@tanstack/react-query'
import { Button, message, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import {
  usePersonsServiceGetPersonByIdEndpointKey,
  usePersonsServiceUpdatePersonEndpoint
} from 'src/api/queries'
import {
  ApiError,
  GetPersonByIdPenitentiaryRecord,
  GetPersonByIdResponse,
  UpdatePersonRequest
} from 'src/api/requests'
import {
  CardWithTitle,
  DeleteConfirm,
  TableActionButtons
} from 'src/components'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { PrisonModal } from './PrisonModal'

type Props = {
  person?: GetPersonByIdResponse
  isEditing: boolean
}

export const PrisonCard = ({ person, isEditing }: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)

  const onToggle = () => setIsOpen((prev) => !prev)

  const { mutate, isPending } = usePersonsServiceUpdatePersonEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetPersonByIdEndpointKey]
      })
      message.success(t('persons.delete_penitentiary_record_successfully'))
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const getActionItems = (index: number) => {
    return [
      {
        key: 'delete',
        label: (
          <DeleteConfirm
            title={t('persons.delete_penitentiary_record')}
            onConfirm={() => {
              const updatedRecords = [...(person?.penitentiaryRecords ?? [])]

              updatedRecords.splice(index, 1)

              mutate({
                id: String(person?.id),
                requestBody: {
                  ...(person as UpdatePersonRequest),
                  penitentiaryRecords: updatedRecords
                }
              })
            }}
          />
        ),
        danger: true
      }
    ]
  }

  const columns: ColumnsType<GetPersonByIdPenitentiaryRecord> = [
    {
      title: t('cases.prison'),
      dataIndex: 'prisonName',
      render: (_, { prisonName }) => prisonName || '-'
    },
    {
      title: t('analysis.date'),
      render: (_, { fromDate }) =>
        dayjs(fromDate).format(dayMonthYearFormatWithDot)
    },
    {
      title: t('analysis.date'),
      render: (_, { toDate }) => dayjs(toDate).format(dayMonthYearFormatWithDot)
    },
    {
      key: 'actions',
      title: t('common.action'),
      align: 'center',
      render: (_, __, index) => (
        <TableActionButtons justify="center" items={getActionItems(index)} />
      )
    }
  ]

  return (
    <CardWithTitle
      title={t('cases.prison')}
      extra={
        <Button disabled={isPending || !isEditing} onClick={() => onToggle()}>
          {t('form.add_prison')}
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={person?.penitentiaryRecords}
        pagination={false}
        bordered
        size="small"
      />

      {isOpen && <PrisonModal onCancel={onToggle} />}
    </CardWithTitle>
  )
}
